import { Card, Col, Form, Row } from 'react-bootstrap';
import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';

import { RELATORIO_CORES } from '../constants/relatorio-cores';

import { ReadDefensorResponseDto } from '@/api.types';
import { EnumerateDefensores } from '@/modules/people/defensor.service';

interface RelatoriosDefensorSelectorProps {
  defensorId?: number | null;
  onSelecionar: (defensor: ReadDefensorResponseDto | null) => void;
  disabled?: boolean;
}

interface OpcaoDefensor {
  value: number;
  label: string;
}

/** Filtro de defensor para perfis administrativos (Administrador, Corregedoria e Recursos Humanos). */
export default function RelatoriosDefensorSelector({
  defensorId,
  onSelecionar,
  disabled = false,
}: RelatoriosDefensorSelectorProps) {
  const [defensores, setDefensores] = useState<ReadDefensorResponseDto[]>([]);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    setCarregando(true);
    EnumerateDefensores(0, 10000)
      .then((resultado) => setDefensores(resultado.objects))
      .catch(() => toast.error('Erro ao carregar a lista de defensores.'))
      .finally(() => setCarregando(false));
  }, []);

  const opcoes = useMemo<OpcaoDefensor[]>(
    () =>
      defensores
        .map((item) => ({ value: item.id, label: item.pessoa?.nome || `Defensor #${item.id}` }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [defensores],
  );

  const cardStyle = {
    border: `1px solid ${RELATORIO_CORES.BORDA_CARD}`,
    borderRadius: '8px',
    boxShadow: RELATORIO_CORES.SOMBRA_CARD,
  };
  const headerStyle = {
    backgroundColor: RELATORIO_CORES.CINZA_CLARO,
    color: RELATORIO_CORES.PRIMARIA,
    borderBottom: `1px solid ${RELATORIO_CORES.BORDA_CARD}`,
    borderRadius: '8px 8px 0 0',
  };

  return (
    <Card className="mb-4" style={cardStyle}>
      <Card.Header style={headerStyle}>
        <h5 className="mb-0 fw-bold" style={{ color: RELATORIO_CORES.PRIMARIA }}>
          Consultar Relatórios por Defensor(a)
        </h5>
      </Card.Header>
      <Card.Body style={{ backgroundColor: RELATORIO_CORES.BRANCO }}>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold text-muted">Defensor(a)</Form.Label>
              <Select<OpcaoDefensor>
                inputId="relatorios-filtro-defensor"
                options={opcoes}
                value={opcoes.find((opcao) => opcao.value === defensorId) ?? null}
                placeholder={carregando ? 'Carregando defensores...' : 'Selecione um defensor...'}
                isLoading={carregando}
                isDisabled={disabled || carregando}
                isClearable
                isSearchable
                noOptionsMessage={() => 'Nenhum defensor encontrado'}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                onChange={(opcao) => {
                  const selecionado = opcao
                    ? defensores.find((item) => item.id === opcao.value) ?? null
                    : null;
                  onSelecionar(selecionado);
                }}
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
